import React, { useState } from 'react';
import './DisambiguationPanel.css';

interface CompanyCandidate {
  legal_name: string;
  country?: string | null;
  city?: string | null;
  full_address?: string | null;
  website?: string | null;
  industry?: string | null;
  description?: string | null;
  confidence?: string;
}

function safeExternalUrl(url: string): string | null {
  try {
    const parsed = new URL(url.startsWith('http') ? url : `https://${url}`);
    return ['http:', 'https:'].includes(parsed.protocol) ? parsed.href : null;
  } catch {
    return null;
  }
}

interface DisambiguationPanelProps {
  candidates: CompanyCandidate[];
  reason?: string;
  companyName: string;
  sources?: Array<{ title: string; url: string }>;
  onConfirm: (selected: CompanyCandidate[], excluded: CompanyCandidate[]) => void;
  onBack: () => void;
}

const DisambiguationPanel: React.FC<DisambiguationPanelProps> = ({
  candidates,
  reason,
  companyName,
  sources,
  onConfirm,
  onBack,
}) => {
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(new Set());
  const [excludedIndices, setExcludedIndices] = useState<Set<number>>(new Set());

  const toggleSelect = (index: number) => {
    setSelectedIndices(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
        // Un-exclude if it was excluded
        setExcludedIndices(ex => {
          const nextEx = new Set(ex);
          nextEx.delete(index);
          return nextEx;
        });
      }
      return next;
    });
  };

  const toggleExclude = (index: number) => {
    setExcludedIndices(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
        // Un-select if it was selected
        setSelectedIndices(sel => {
          const nextSel = new Set(sel);
          nextSel.delete(index);
          return nextSel;
        });
      }
      return next;
    });
  };

  const handleConfirm = () => {
    const selected = candidates.filter((_, i) => selectedIndices.has(i));
    const excluded = candidates.filter((_, i) => excludedIndices.has(i));
    onConfirm(selected, excluded);
  };

  const selectedCount = selectedIndices.size;
  const nonExcludedCount = candidates.length - excludedIndices.size;
  const allNonExcludedSelected = selectedCount > 0 && selectedCount === nonExcludedCount;

  const toggleSelectAll = () => {
    if (allNonExcludedSelected) {
      // Deselect all
      setSelectedIndices(new Set());
    } else {
      // Select all non-excluded
      const next = new Set<number>();
      candidates.forEach((_, i) => {
        if (!excludedIndices.has(i)) next.add(i);
      });
      setSelectedIndices(next);
    }
  };

  return (
    <div className="disambiguation-panel">
      <div className="disambiguation-header">
        <h2>Which company did you mean?</h2>
        <p className="disambiguation-subtitle">
          We found multiple companies matching "<strong>{companyName}</strong>".
          {reason && <span className="disambiguation-reason"> {reason}.</span>}
        </p>
        <p className="disambiguation-hint">
          Select one or more companies to screen together, or exclude those you know are unrelated.
        </p>
      </div>

      <div className="select-all-row">
        <label className="select-all-label" onClick={toggleSelectAll}>
          <span className={`card-checkbox${allNonExcludedSelected ? ' checked' : ''}`}>
            {allNonExcludedSelected && (
              <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true">
                <path d="M13.5 4.5l-7 7L3 8" stroke="white" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
          </span>
          <span>Select All ({candidates.length - excludedIndices.size})</span>
        </label>
      </div>

      <div className="disambiguation-cards">
        {candidates.map((candidate, index) => {
          const isSelected = selectedIndices.has(index);
          const isExcluded = excludedIndices.has(index);

          if (isExcluded) {
            return (
              <div
                key={index}
                className="disambiguation-card excluded"
              >
                <div className="excluded-inline">
                  <span className="excluded-name">{candidate.legal_name}</span>
                  {candidate.country && (
                    <span className="excluded-country"> ({candidate.country})</span>
                  )}
                  <button
                    className="undo-exclude-btn"
                    onClick={() => toggleExclude(index)}
                    type="button"
                  >
                    Undo
                  </button>
                </div>
              </div>
            );
          }

          return (
            <div
              key={index}
              className={`disambiguation-card${isSelected ? ' selected' : ''}`}
              onClick={() => toggleSelect(index)}
            >
              <button
                className="card-exclude-btn"
                onClick={(e) => { e.stopPropagation(); toggleExclude(index); }}
                type="button"
                title="Exclude this company"
                aria-label={`Exclude ${candidate.legal_name}`}
              >
                &times;
              </button>
              <div className="card-select-area">
                <span className={`card-checkbox${isSelected ? ' checked' : ''}`}>
                  {isSelected && (
                    <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true">
                      <path d="M13.5 4.5l-7 7L3 8" stroke="white" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </span>
                <div className="card-main">
                  <h3 className="card-name">{candidate.legal_name}</h3>
                  <div className="card-details">
                    {(candidate.city || candidate.country) && (
                      <div className="card-detail location">
                        <span className="detail-icon">&#x1F4CD;</span>
                        <span>
                          {[candidate.city, candidate.country].filter(Boolean).join(', ')}
                        </span>
                      </div>
                    )}
                    {candidate.industry && (
                      <div className="card-detail industry">
                        <span className="detail-icon">&#x1F3ED;</span>
                        <span>{candidate.industry}</span>
                      </div>
                    )}
                    {candidate.website && safeExternalUrl(candidate.website) && (
                      <div className="card-detail website">
                        <span className="detail-icon">&#x1F310;</span>
                        <a
                          href={safeExternalUrl(candidate.website)!}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {candidate.website}
                        </a>
                      </div>
                    )}
                  </div>
                  {candidate.description && (
                    <p className="card-description">{candidate.description}</p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="disambiguation-actions">
        <button
          className="confirm-selection-btn"
          onClick={handleConfirm}
          disabled={selectedCount === 0}
          type="button"
        >
          Screen Selected{selectedCount > 0 && (
            <span className="selection-count">{selectedCount}</span>
          )}
        </button>
        <button
          className="disambiguation-back"
          onClick={onBack}
          type="button"
        >
          None of these &mdash; refine your search
        </button>
      </div>

      {sources && sources.length > 0 && (
        <div className="disambiguation-sources">
          <h4 className="sources-heading">Sources</h4>
          <ul className="sources-list">
            {sources.map((src, i) => (
              <li key={i}>
                <a href={src.url} target="_blank" rel="noopener noreferrer">
                  {src.title || src.url}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default DisambiguationPanel;
